import React, { useContext, useState, createContext, useEffect, ReactNode, useMemo } from 'react';
import { AlertColor } from '@mui/material';
import { Tag, User } from '@sm-dashboard/reshet-types';
import instanceAxios from '@/util/axios';
import { getStaffMemberByIdUrl, isNewLeadsCountUrl, staffMembersAllUrl } from '@/constants';
import { useJsApiLoader } from '@react-google-maps/api';
import { UserPermission } from '@/util/Permission';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '@/app/app';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { AxiosError } from 'axios';
import { useTags } from '@/api/tags';
import { useStaffMember } from '@/api/staffMembers';
import { queryClient } from '@/react-query';

type SnackbarType = {
  message: string | ReactNode;
  open: boolean;
  severity: AlertColor;
};

export type AutocompleteType = {
  label: string;
  value: string;
};

type OpenControl = {
  openDrawer: boolean;
  openChildMenu: boolean;
};

type Link = {
  primary: string;
  link: string;
};

export type Institution = {
  label: string;
  value: number;
};

type AppContextType = {
  user: User | undefined;
  setUser: React.Dispatch<React.SetStateAction<User | undefined>>;
  isNewLeadsCount: number;
  setIsNewLeadsCount: React.Dispatch<React.SetStateAction<number>>;
  snackbar: SnackbarType;
  setSnackbar: React.Dispatch<React.SetStateAction<SnackbarType>>;
  selectedNav: Link;
  setSelectedNav: React.Dispatch<React.SetStateAction<Link>>;
  allStaffMembers: AutocompleteType[];
  setAllStaffMembers: React.Dispatch<React.SetStateAction<AutocompleteType[]>>;
  isGoogleMapsApiLoaded: boolean;
  googleMapsApiLoadError: Error | undefined;
  openControl: OpenControl;
  setOpenControl: React.Dispatch<React.SetStateAction<OpenControl>>;
  previewProfile: string | null;
  setPreviewProfile: React.Dispatch<React.SetStateAction<string | null>>;
  staffMember: User | undefined;
  userPermission: UserPermission;
  tags: Tag[];
  scrollPos: { [key: string]: number };
  setScrollPos: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>;
};

const AppContext = createContext<AppContextType>(null!);
export const useAppContext = () => useContext(AppContext);

// Add this constant outside the component
const GOOGLE_MAPS_LIBRARIES: ('places' | 'drawing')[] = ['places', 'drawing'];

export const ContextProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User>();
  const [isNewLeadsCount, setIsNewLeadsCount] = useState<number>(0);
  const [snackbar, setSnackbar] = useState<SnackbarType>({ message: '', open: false, severity: 'info' });
  const [allStaffMembers, setAllStaffMembers] = useState<AutocompleteType[]>([]);
  const [openControl, setOpenControl] = useState({ openDrawer: false, openChildMenu: true });
  const [previewProfile, setPreviewProfile] = useState<string | null>(null);
  const [staffMember, setStaffMember] = useState<User>();
  const [userPermission, setUserPermission] = useState<UserPermission>(null!);
  const { isLoaded: isGoogleMapsApiLoaded, loadError: googleMapsApiLoadError } = useJsApiLoader({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    googleMapsApiKey: (import.meta as any).env.VITE_GOOGLE_MAPS_KEY,
    language: 'iw',
    libraries: GOOGLE_MAPS_LIBRARIES
  });
  const [scrollPos, setScrollPos] = useState<{ [key: string]: number }>({});

  const { tags } = useTags();
  const { staffMember: userData } = useStaffMember();

  const normalizedPath = useMemo(() => {
    const p = location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1).toLowerCase()
      : location.pathname.toLowerCase();
    return p || '/';
  }, [location.pathname]);

  const [selectedNav, setSelectedNav] = useState({ link: '', primary: '' });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem('reshet-token');

        if (!token) {
          if (normalizedPath === '/login' || normalizedPath === '/register' || normalizedPath === '/intro' || normalizedPath.includes('upload-reference')) {
            return;
          }
          navigate('/login');
          return;
        }

        if (userData?.reLogin) {
          window.localStorage.removeItem('reshet-token');
          setUser(undefined);
        }

        if (userData?.id) {
          setUserPermission(new UserPermission(tags || [], userData?.tags || []));
          queryClient.invalidateQueries();
        }

        const { data: leadsData } = await instanceAxios.get(isNewLeadsCountUrl);
        const { data: staffMembersData } = await instanceAxios.get(staffMembersAllUrl);
        setAllStaffMembers(staffMembersData);
        setIsNewLeadsCount(leadsData);
        setStaffMember(userData);

        if (normalizedPath === '/register') {
          navigate('/');
        }
      } catch (error: AxiosError | unknown) {
        console.log(error);
      }
    };
    fetchInitialData();
  }, [userData, navigate, tags, normalizedPath]);

  useEffect(() => {
    setSelectedNav({ link: normalizedPath, primary: getPageName(normalizedPath) });
  }, [normalizedPath]);

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        isNewLeadsCount,
        setIsNewLeadsCount,
        snackbar,
        setSnackbar,
        selectedNav,
        setSelectedNav,
        allStaffMembers,
        setAllStaffMembers,
        isGoogleMapsApiLoaded,
        googleMapsApiLoadError,
        openControl,
        setOpenControl,
        previewProfile,
        setPreviewProfile,
        staffMember,
        userPermission,
        tags,
        scrollPos,
        setScrollPos
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const getPageName = (pathname: string): string => {
  const flattenPagesList = routes.map(route => ({
    primary: route.primary,
    link: route.route.toLowerCase()
  }));
  // Remove trailing slash if present
  const normalizedPath = pathname.endsWith('/') ? pathname.slice(0, -1).toLowerCase() : pathname.toLowerCase();

  // Check for exact match first
  if (flattenPagesList.find(page => page.link === normalizedPath)) {
    return flattenPagesList.find(page => page.link === normalizedPath)?.primary || 'דשבורד רשת';
  }

  // Check for partial matches for nested routes like '/crm/contact/:id'
  for (const route of flattenPagesList) {
    if (normalizedPath.startsWith(route.link) && route.link !== '/') {
      return route.primary || 'דשבורד רשת';
    }
  }

  // Split the path and get the first part (e.g., '/shluchim/123' becomes '/shluchim')
  const basePath = '/' + normalizedPath.split('/')[1];

  return flattenPagesList.find(page => page.link === basePath)?.primary || 'דשבורד רשת';
};
